import { useState, useCallback, useRef } from "react";
import { useAuth } from "@clerk/clerk-react";
import { ApiKey } from "../types/api";
import { apiService } from "../services/api";

interface ApiKeyState {
  data: ApiKey[];
  isLoading: boolean;
  error: string | null;
}

const initialState: ApiKeyState = {
  data: [],
  isLoading: true,
  error: null,
};

export const useApiKeys = () => {
  const { getToken } = useAuth();
  const [state, setState] = useState<ApiKeyState>(initialState);

  const getValidToken = useCallback(async () => {
    const token = await getToken();
    if (!token) {
      throw new Error(
        "Authentication token not available - please ensure you are logged in",
      );
    }
    return token;
  }, [getToken]);

  const hasFetched = useRef(false);

  const fetchKeys = useCallback(
    async (force = false) => {
      if (hasFetched.current && !force) {
        return;
      }
      hasFetched.current = true;
      try {
        setState((prev) => ({ ...prev, isLoading: true, error: null }));
        const token = await getValidToken();
        const data = await apiService.fetchKeys(token);
        setState({ data, isLoading: false, error: null });
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load API keys";
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }));
        console.error("Error fetching API keys:", err);
      }
    },
    [getValidToken],
  );

  const createKey = useCallback(
    async (name: string) => {
      try {
        setState((prev) => ({ ...prev, error: null }));
        const token = await getValidToken();
        const result = await apiService.createKey(token, name);
        await fetchKeys(true); // Force refetch to get updated data
        return { success: true, key: result.key };
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to create API key";
        setState((prev) => ({ ...prev, error: errorMessage }));
        console.error("Error creating API key:", err);
        return { success: false };
      }
    },
    [getValidToken, fetchKeys],
  );

  const deleteKey = useCallback(
    async (keyId: string) => {
      try {
        // Optimistically update UI
        setState((prev) => ({
          ...prev,
          error: null,
          data: prev.data.filter((key) => key.id !== keyId),
        }));

        const token = await getValidToken();
        await apiService.deleteKey(token, keyId);
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to delete API key";
        setState((prev) => ({ ...prev, error: errorMessage }));
        // Revert optimistic update
        await fetchKeys(true); // Force refetch to revert optimistic update
        return false;
      }
    },
    [getValidToken, fetchKeys],
  );

  return {
    apiKeys: state.data,
    isLoading: state.isLoading,
    error: state.error,
    fetchKeys,
    createKey,
    deleteKey,
  };
};
